import { Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { HOURS } from "@/lib/constants";

// Helper: convert 24h "HH:MM" into a nicely formatted time string per locale.
const formatTime = (t24, locale) => {
  if (!t24) return "";
  const [h, m] = t24.split(":").map(Number);
  if (locale === "ar") {
    const period = h < 12 ? "ص" : "م";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${period}`;
  }
  const period = h < 12 ? "AM" : "PM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

const DAY_LABELS_EN = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
};

const LocationHours = () => {
  const { t, lang } = useLanguage();
  const isAR = lang === "ar";

  // today's index — JS Sunday=0, matches array order
  const todayIdx = new Date().getDay();

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#111] p-6 md:p-7"
      data-testid="location-hours"
    >
      <div className="flex items-center gap-2 mb-5">
        <Clock className="w-4 h-4 text-[#e10600]" />
        <h3 className="font-display font-bold text-base md:text-lg text-white">
          {t.location.hoursTitle}
        </h3>
      </div>

      {HOURS.ramadan.active && (
        <div className="mb-5 rounded-xl border border-[#e10600]/30 bg-[#e10600]/5 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#e10600] font-display mb-2">
            {t.location.ramadanLabel}
          </p>
          <div className="flex flex-col gap-1 text-sm text-white/85">
            {HOURS.ramadan.slots.map((s) => (
              <span key={`${s.open}-${s.close}`}>
                {formatTime(s.open, lang)} — {formatTime(s.close, lang)}
              </span>
            ))}
          </div>
        </div>
      )}

      <ul className="divide-y divide-white/5">
        {HOURS.regular.map((d, i) => {
          const isToday = i === todayIdx;
          return (
            <li
              key={d.day}
              className={`flex items-center justify-between py-2.5 text-sm ${
                isToday ? "text-white" : "text-white/65"
              }`}
            >
              <span className="font-semibold inline-flex items-center gap-2">
                {isAR ? d.ar : DAY_LABELS_EN[d.day]}
                {isToday && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase font-display bg-[#e10600]/15 text-[#e10600] rounded-full border border-[#e10600]/30">
                    {t.location.todayLabel}
                  </span>
                )}
              </span>
              {d.closed ? (
                <span className="text-white/50">{t.location.closed}</span>
              ) : (
                <span className="tabular-nums">
                  {formatTime(d.open, lang)}–{formatTime(d.close, lang)}
                  {d.open2 && (
                    <>
                      <span className="text-white/30 mx-1.5">·</span>
                      {formatTime(d.open2, lang)}–{formatTime(d.close2, lang)}
                    </>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="mt-5 text-[11px] text-white/40 leading-relaxed">
        {HOURS.note[lang] || HOURS.note.en}
      </p>
    </div>
  );
};

export default LocationHours;
